const STABLE_HISTORY_TRANSFER_ID_API_MIN = 'BAG';
export const HISTORY_RECOVERY_RETRY_DELAY_MS = 3000;
export const HISTORY_INACTIVITY_TIMEOUT_MS = 7000;
const MAX_ABANDONED_TRANSFER_IDS = 8;

const HISTORY_RESPONSE_BY_RESOLUTION = {
  4: 'send_recent_history',
  5: 'send_recent_history',
  240: 'send_daily_history',
  900: 'send_history'
};

export function normalizeHistoryResolution(resolution) {
  const numericResolution = Number(resolution);
  if (numericResolution === 1800) return 900;
  if (numericResolution in HISTORY_RESPONSE_BY_RESOLUTION) return numericResolution;
  return 240;
}

export function expectedHistoryResponse(resolution) {
  return HISTORY_RESPONSE_BY_RESOLUTION[normalizeHistoryResolution(resolution)];
}

function getAbandonedTransferIds(history) {
  const ids = Array.isArray(history?.abandoned_transfer_ids)
    ? [...history.abandoned_transfer_ids]
    : [];
  if (
    history?.abandoned_transfer_id !== null &&
    history?.abandoned_transfer_id !== undefined &&
    !ids.includes(history.abandoned_transfer_id)
  ) {
    ids.push(history.abandoned_transfer_id);
  }
  return ids;
}

export function beginHistoryRequest(
  history,
  resolution,
  { recoveryRetry = false, preserveAbandonedTransfers = false } = {}
) {
  const normalizedResolution = normalizeHistoryResolution(resolution);
  history.resolution = normalizedResolution;
  history.request_in_flight = true;
  history.request_activity_at_ms = Date.now();
  history.abandoned_transfer_ids =
    recoveryRetry || preserveAbandonedTransfers ? getAbandonedTransferIds(history) : [];
  // Normalize state restored from versions that stored only one abandoned ID.
  if ('abandoned_transfer_id' in history) history.abandoned_transfer_id = null;
  history.recovery_retry_count = recoveryRetry
    ? (Number(history.recovery_retry_count) || 0) + 1
    : 0;
}

export function cancelHistoryRequest(history) {
  history.request_in_flight = false;
}

export function markHistoryChunkReceived(history) {
  history.request_activity_at_ms = Date.now();
}

export function abandonHistoryTransfer(history) {
  if (history.pending_transfer_id !== null && history.pending_transfer_id !== undefined) {
    const ids = getAbandonedTransferIds(history);
    if (!ids.includes(history.pending_transfer_id)) ids.push(history.pending_transfer_id);
    history.abandoned_transfer_ids = ids.slice(-MAX_ABANDONED_TRANSFER_IDS);
  }
}

export function shouldRetryHistoryRequest(history) {
  return Boolean(
    history?.request_in_flight &&
    history.incomplete_final_chunk &&
    (Number(history.recovery_retry_count) || 0) === 0
  );
}

export function shouldAcceptHistoryChunk(history, command, data = {}) {
  const abandonedTransferIds = getAbandonedTransferIds(history);
  return Boolean(
    history?.request_in_flight &&
    command === expectedHistoryResponse(history.resolution) &&
    !abandonedTransferIds.includes(data.timestamp)
  );
}

export function completeHistoryRequest(history) {
  if (history?.progress === 100 && !history.incomplete_final_chunk) {
    cancelHistoryRequest(history);
    history.abandoned_transfer_ids = [];
    if ('abandoned_transfer_id' in history) history.abandoned_transfer_id = null;
    return true;
  }
  return false;
}

export function supportsStableHistoryTransferId(controller) {
  const apiVersion = controller?.api_version;
  return (
    typeof apiVersion === 'string' && apiVersion.toUpperCase() >= STABLE_HISTORY_TRANSFER_ID_API_MIN
  );
}

export function alignDownToHistoryResolution(timestamp, resolutionSeconds) {
  const bucketMs = Number.parseInt(resolutionSeconds, 10) * 1000;
  if (!Number.isFinite(timestamp) || !Number.isFinite(bucketMs) || bucketMs <= 0) {
    return timestamp;
  }
  return timestamp - (timestamp % bucketMs);
}
